import { useAction, useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import { LoaderCircleIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useLocation } from "react-router";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { api } from "../../../convex/_generated/api";
import type { Provider } from "../../../convex/lib/schema/integrations";

const GMAIL_ICON_URL =
	"https://upload.wikimedia.org/wikipedia/commons/7/7e/Gmail_icon_%282020%29.svg";
const GOOGLE_SHEETS_ICON_URL =
	"https://upload.wikimedia.org/wikipedia/commons/a/ae/Google_Sheets_2020_Logo.svg";
const OUTLOOK_ICON_URL =
	"https://upload.wikimedia.org/wikipedia/commons/c/cc/Microsoft_Outlook_Icon_%282025%E2%80%93present%29.svg";
const CLIO_ICON_URL =
	"https://www.google.com/s2/favicons?domain=clio.com&sz=128";
const DOCKETWISE_ICON_URL =
	"https://www.google.com/s2/favicons?domain=docketwise.com&sz=128";
const INSZOOM_ICON_URL =
	"https://www.google.com/s2/favicons?domain=inszoom.com&sz=128";
const TEAMS_ICON_URL =
	"https://upload.wikimedia.org/wikipedia/commons/0/07/Microsoft_Office_Teams_%282025%E2%80%93present%29.svg";
const ADOBE_SIGN_ICON_URL =
	"https://www.google.com/s2/favicons?domain=adobe.com&sz=128";

function ConnectionAvatar({
	src,
	alt,
	fallback,
}: {
	src: string;
	alt: string;
	fallback: string;
}) {
	return (
		<Avatar size="lg" className="rounded-md bg-muted after:rounded-md">
			<AvatarImage
				src={src}
				alt={alt}
				className="rounded-md object-contain p-1.5"
			/>
			<AvatarFallback className="rounded-md">{fallback}</AvatarFallback>
		</Avatar>
	);
}

function extractErrorMessage(error: unknown, fallback: string): string {
	if (error instanceof ConvexError && typeof error.data === "string") {
		return error.data;
	}
	if (error instanceof Error) {
		return error.message;
	}
	return fallback;
}

type ConnectionCard = {
	id: string;
	provider: Provider;
	name: string;
	description: string;
	detail: string;
	connected: boolean;
	icon: React.ReactNode;
	disabledReason?: string;
};

function parseLocationState(
	state: unknown,
): { googleConnectionStatus?: "connected" } | null {
	if (
		state !== null &&
		typeof state === "object" &&
		"googleConnectionStatus" in state
	) {
		return state as { googleConnectionStatus?: "connected" };
	}
	return null;
}

function useBasePath() {
	const { pathname } = useLocation();
	return pathname.startsWith("/dev/workspace")
		? "/dev/workspace"
		: "/workspace";
}

export default function ConnectionsPage() {
	const location = useLocation();
	const basePath = useBasePath();
	const gmailStatus = useQuery(
		api.integrations.connections.getConnectionStatus,
		{ provider: "gmail" },
	);
	const sheetsStatus = useQuery(
		api.integrations.connections.getConnectionStatus,
		{ provider: "google-sheets" },
	);
	const outlookStatus = useQuery(
		api.integrations.connections.getConnectionStatus,
		{ provider: "ms-outlook" },
	);
	const clioStatus = useQuery(
		api.integrations.connections.getFirmConnectionStatus,
		{ provider: "clio" },
	);
	const docketwiseStatus = useQuery(
		api.integrations.connections.getFirmConnectionStatus,
		{ provider: "docketwise" },
	);
	const inszoomStatus = useQuery(
		api.integrations.connections.getFirmConnectionStatus,
		{ provider: "inszoom" },
	);
	const teamsStatus = useQuery(
		api.integrations.connections.getFirmConnectionStatus,
		{ provider: "ms-teams" },
	);
	const adobeSignStatus = useQuery(
		api.integrations.connections.getFirmConnectionStatus,
		{ provider: "adobe-sign" },
	);
	const teamsPackage = useQuery(
		api.integrations.teams.package.isPackageAvailable,
		{},
	);
	const teamsAppDownloadUrl = (() => {
		const base = (import.meta.env.VITE_CONVEX_SITE_URL ?? "").trim();
		if (!base) return null;
		return `${base.replace(/\/$/, "")}/api/teams-app.zip`;
	})();
	const beginOAuth = useMutation(api.integrations.connections.beginOAuth);
	const disconnectMutation = useMutation(
		api.integrations.connections.disconnect,
	);
	const docketwiseConnectAction = useAction(
		api.integrations.docketwise.connection.connect,
	);
	const docketwiseDisconnectMutation = useMutation(
		api.integrations.docketwise.connection.disconnect,
	);
	const inszoomConnectAction = useAction(
		api.integrations.inszoom.connection.connect,
	);
	const inszoomDisconnectMutation = useMutation(
		api.integrations.inszoom.connection.disconnect,
	);
	const [pendingProvider, setPendingProvider] = useState<Provider | null>(null);
	const [pendingAction, setPendingAction] = useState<
		"connect" | "disconnect" | null
	>(null);
	// Surface Adobe Sign's server-side OAuth callback once, then strip its params.
	const [localMessage, setLocalMessage] = useState<string | null>(() => {
		const params = new URLSearchParams(window.location.search);
		const ok = params.get("adobe-sign");
		const err = params.get("adobe-sign-error");
		let message: string | null = null;
		if (ok === "connected") {
			message = "Adobe Acrobat Sign connected.";
		} else if (err) {
			message = `Adobe Acrobat Sign couldn't connect: ${err}`;
		}
		if (ok || err) {
			params.delete("adobe-sign");
			params.delete("adobe-sign-error");
			const search = params.toString();
			window.history.replaceState(
				null,
				"",
				`${window.location.pathname}${search ? `?${search}` : ""}`,
			);
		}
		return message;
	});
	const locationState = parseLocationState(location.state);

	const personalCards = useMemo<ConnectionCard[]>(() => {
		const gmailConnected = gmailStatus?.status === "connected";
		const sheetsConnected = sheetsStatus?.status === "connected";
		const outlookConnected = outlookStatus?.status === "connected";
		const gmailAccount =
			gmailStatus?.accountEmail ?? "No Gmail account connected";
		const sheetsAccount =
			sheetsStatus?.accountEmail ?? "No Google account connected";
		const outlookAccount =
			outlookStatus?.accountEmail ?? "No Outlook account connected";
		return [
			{
				id: "gmail",
				provider: "gmail",
				name: "Gmail",
				description:
					"Connect a Gmail inbox to trigger workflows automatically when new messages arrive.",
				detail: gmailAccount,
				connected: gmailConnected,
				icon: (
					<ConnectionAvatar src={GMAIL_ICON_URL} alt="Gmail" fallback="G" />
				),
			},
			{
				id: "google-sheets",
				provider: "google-sheets",
				name: "Google Sheets",
				description:
					"Connect Google Sheets to read and write spreadsheet data from workflows.",
				detail: sheetsAccount,
				connected: sheetsConnected,
				icon: (
					<ConnectionAvatar
						src={GOOGLE_SHEETS_ICON_URL}
						alt="Google Sheets"
						fallback="S"
					/>
				),
			},
			{
				id: "ms-outlook",
				provider: "ms-outlook",
				name: "Microsoft Outlook",
				description:
					"Connect an Outlook mailbox to receive email notifications and auto-draft replies.",
				detail: outlookAccount,
				connected: outlookConnected,
				icon: (
					<ConnectionAvatar
						src={OUTLOOK_ICON_URL}
						alt="Microsoft Outlook"
						fallback="O"
					/>
				),
			},
		];
	}, [gmailStatus, sheetsStatus, outlookStatus]);

	const firmCards = useMemo<ConnectionCard[]>(() => {
		function firmDetail(
			status:
				| {
						status: "connected" | "disconnected";
						accountEmail?: string;
						connectedByName?: string;
				  }
				| undefined,
			placeholder: string,
		) {
			if (status?.status !== "connected") return placeholder;
			const name = status.connectedByName;
			const email = status.accountEmail;
			if (name && email) return `Connected by ${name} · ${email}`;
			if (name) return `Connected by ${name}`;
			if (email) return `Connected as ${email}`;
			return "Connected";
		}
		function firmDisabledReason(
			status:
				| {
						status: "connected" | "disconnected";
						connectedByName?: string;
						canDisconnect: boolean;
				  }
				| undefined,
		): string | undefined {
			if (status?.status !== "connected" || status.canDisconnect)
				return undefined;
			return status.connectedByName
				? `${status.connectedByName} has already connected this. Ask them to disconnect first.`
				: "This was connected by another user. Ask a Clerical staff member to disconnect it.";
		}
		function teamsDetail(
			status:
				| {
						status: "connected" | "disconnected";
						accountEmail?: string;
						connectedByName?: string;
						msTenantId?: string;
				  }
				| undefined,
		): string {
			if (status?.status !== "connected") {
				return "Not connected — sideload the Clerical Teams app to start.";
			}
			if (status.connectedByName || status.accountEmail) {
				return firmDetail(status, "Connected");
			}
			return `Connected · Microsoft tenant ${status.msTenantId ?? "(unknown)"}`;
		}
		return [
			{
				id: "clio",
				provider: "clio",
				name: "Clio Manage",
				description:
					"Connect Clio Manage to look up matters, file documents, and manage contacts.",
				detail: firmDetail(clioStatus, "No Clio account connected"),
				connected: clioStatus?.status === "connected",
				icon: <ConnectionAvatar src={CLIO_ICON_URL} alt="Clio" fallback="C" />,
				disabledReason: firmDisabledReason(clioStatus),
			},
			{
				id: "docketwise",
				provider: "docketwise",
				name: "Docketwise",
				description:
					"Connect Docketwise to look up matters, file documents, and manage contacts.",
				detail: firmDetail(docketwiseStatus, "No Docketwise account connected"),
				connected: docketwiseStatus?.status === "connected",
				icon: (
					<ConnectionAvatar
						src={DOCKETWISE_ICON_URL}
						alt="Docketwise"
						fallback="D"
					/>
				),
				disabledReason: firmDisabledReason(docketwiseStatus),
			},
			{
				id: "inszoom",
				provider: "inszoom",
				name: "MiraTech INSZoom",
				description:
					"Connect MiraTech INSZoom to look up matters, file documents, and manage contacts.",
				detail: firmDetail(
					inszoomStatus,
					"No MiraTech INSZoom account connected",
				),
				connected: inszoomStatus?.status === "connected",
				icon: (
					<ConnectionAvatar
						src={INSZOOM_ICON_URL}
						alt="MiraTech INSZoom"
						fallback="I"
					/>
				),
				disabledReason: firmDisabledReason(inszoomStatus),
			},
			{
				id: "ms-teams",
				provider: "ms-teams",
				name: "Microsoft Teams",
				description:
					"Install the Clerical bot in your firm's Teams workspace so anyone can chat with the agent.",
				detail: teamsDetail(teamsStatus),
				connected: teamsStatus?.status === "connected",
				icon: (
					<ConnectionAvatar
						src={TEAMS_ICON_URL}
						alt="Microsoft Teams"
						fallback="T"
					/>
				),
				disabledReason:
					teamsStatus?.status === "connected"
						? "Microsoft Teams installs are managed in Teams Admin Center — they can't be removed here."
						: undefined,
			},
			{
				id: "adobe-sign",
				provider: "adobe-sign",
				name: "Adobe Acrobat Sign",
				description:
					"Send documents for client e-signature. The agent uploads PDFs and returns the signing link to drop into client emails.",
				detail: firmDetail(
					adobeSignStatus,
					"No Adobe Acrobat Sign account connected",
				),
				connected: adobeSignStatus?.status === "connected",
				icon: (
					<ConnectionAvatar
						src={ADOBE_SIGN_ICON_URL}
						alt="Adobe Acrobat Sign"
						fallback="A"
					/>
				),
				disabledReason: firmDisabledReason(adobeSignStatus),
			},
		];
	}, [
		clioStatus,
		docketwiseStatus,
		inszoomStatus,
		teamsStatus,
		adobeSignStatus,
	]);

	async function handleConnect(provider: Provider) {
		try {
			setPendingProvider(provider);
			setPendingAction("connect");
			setLocalMessage(null);

			// Adobe Sign requires HTTPS-only redirect URIs — callback is handled
			// server-side at <deployment>.convex.site, not a React page.
			let redirectUri: string;
			if (provider === "adobe-sign") {
				const siteBase = (import.meta.env.VITE_CONVEX_SITE_URL ?? "").trim();
				if (!siteBase) {
					throw new Error(
						"VITE_CONVEX_SITE_URL is not configured — needed for Adobe Sign OAuth",
					);
				}
				redirectUri = `${siteBase.replace(/\/$/, "")}/oauth/adobe-sign/callback`;
			} else {
				const callbackPath =
					provider === "ms-outlook"
						? `${basePath}/connections/outlook/callback`
						: provider === "clio"
							? `${basePath}/connections/clio/callback`
							: `${basePath}/connections/google/callback`;
				// Clio requires https or http://127.0.0.1 — no localhost
				const origin =
					provider === "clio"
						? window.location.origin.replace("//localhost:", "//127.0.0.1:")
						: window.location.origin;
				redirectUri = `${origin}${callbackPath}`;
			}

			const result = await beginOAuth({ provider, redirectUri });
			window.location.assign(result.authorizationUrl);
		} catch (error) {
			setLocalMessage(
				error instanceof Error
					? error.message
					: "Unable to start the connection flow.",
			);
			setPendingProvider(null);
			setPendingAction(null);
		}
	}

	async function handleDisconnect(provider: Provider) {
		try {
			setPendingProvider(provider);
			setPendingAction("disconnect");
			setLocalMessage(null);
			await disconnectMutation({ provider });
			setLocalMessage(
				`Disconnected ${provider === "gmail" ? "Gmail" : "Google Sheets"}.`,
			);
		} catch (error) {
			setLocalMessage(
				error instanceof Error
					? error.message
					: "Unable to disconnect the integration.",
			);
		} finally {
			setPendingProvider(null);
			setPendingAction(null);
		}
	}

	const isAnyPending = pendingAction !== null;
	const isLoading =
		gmailStatus === undefined ||
		sheetsStatus === undefined ||
		outlookStatus === undefined ||
		clioStatus === undefined ||
		docketwiseStatus === undefined ||
		inszoomStatus === undefined ||
		teamsStatus === undefined;

	const [teamsDialogOpen, setTeamsDialogOpen] = useState(false);

	const [dwDialogOpen, setDwDialogOpen] = useState(false);
	const [dwUsername, setDwUsername] = useState("");
	const [dwPassword, setDwPassword] = useState("");
	const [dwConnecting, setDwConnecting] = useState(false);
	const [dwError, setDwError] = useState<string | null>(null);

	function resetDwForm() {
		setDwUsername("");
		setDwPassword("");
		setDwError(null);
	}

	function openDwDialog() {
		resetDwForm();
		setDwDialogOpen(true);
	}

	const [izDialogOpen, setIzDialogOpen] = useState(false);
	const [izUsername, setIzUsername] = useState("");
	const [izPassword, setIzPassword] = useState("");
	const [izConnecting, setIzConnecting] = useState(false);
	const [izError, setIzError] = useState<string | null>(null);

	function resetIzForm() {
		setIzUsername("");
		setIzPassword("");
		setIzError(null);
	}

	function openIzDialog() {
		resetIzForm();
		setIzDialogOpen(true);
	}

	async function handleDocketwiseConnect() {
		if (!dwUsername.trim() || !dwPassword) {
			setDwError("Username and password are required.");
			return;
		}
		try {
			setDwConnecting(true);
			setDwError(null);
			const result = await docketwiseConnectAction({
				username: dwUsername.trim(),
				password: dwPassword,
			});
			setDwDialogOpen(false);
			resetDwForm();
			setLocalMessage(`Connected Docketwise as ${result.accountEmail}.`);
		} catch (error) {
			setDwError(extractErrorMessage(error, "Unable to connect Docketwise."));
		} finally {
			setDwConnecting(false);
		}
	}

	async function handleDocketwiseDisconnect() {
		try {
			setPendingProvider("docketwise");
			setPendingAction("disconnect");
			setLocalMessage(null);
			await docketwiseDisconnectMutation({});
			setLocalMessage("Disconnected Docketwise.");
		} catch (error) {
			setLocalMessage(
				extractErrorMessage(error, "Unable to disconnect Docketwise."),
			);
		} finally {
			setPendingProvider(null);
			setPendingAction(null);
		}
	}

	async function handleInszoomConnect() {
		if (!izUsername.trim() || !izPassword) {
			setIzError("Username and password are required.");
			return;
		}
		try {
			setIzConnecting(true);
			setIzError(null);
			const result = await inszoomConnectAction({
				username: izUsername.trim(),
				password: izPassword,
			});
			setIzDialogOpen(false);
			resetIzForm();
			setLocalMessage(`Connected MiraTech INSZoom as ${result.accountEmail}.`);
		} catch (error) {
			setIzError(
				extractErrorMessage(error, "Unable to connect MiraTech INSZoom."),
			);
		} finally {
			setIzConnecting(false);
		}
	}

	async function handleInszoomDisconnect() {
		try {
			setPendingProvider("inszoom");
			setPendingAction("disconnect");
			setLocalMessage(null);
			await inszoomDisconnectMutation({});
			setLocalMessage("Disconnected MiraTech INSZoom.");
		} catch (error) {
			setLocalMessage(
				extractErrorMessage(error, "Unable to disconnect MiraTech INSZoom."),
			);
		} finally {
			setPendingProvider(null);
			setPendingAction(null);
		}
	}

	function renderCard(conn: ConnectionCard) {
		const isThisActionPending =
			pendingProvider === conn.provider && pendingAction !== null;
		const buttonDisabled =
			isLoading ||
			(isAnyPending && !isThisActionPending) ||
			(conn.disabledReason !== undefined && conn.connected);
		const button = (
			<Button
				variant={conn.connected ? "outline" : "default"}
				size="sm"
				className="min-w-[120px]"
				onClick={() => {
					if (conn.provider === "docketwise") {
						if (conn.connected) {
							handleDocketwiseDisconnect();
						} else {
							openDwDialog();
						}
						return;
					}
					if (conn.provider === "inszoom") {
						if (conn.connected) {
							handleInszoomDisconnect();
						} else {
							openIzDialog();
						}
						return;
					}
					if (conn.provider === "ms-teams") {
						if (conn.connected) {
							handleDisconnect("ms-teams");
						} else {
							setTeamsDialogOpen(true);
						}
						return;
					}
					if (conn.connected) {
						handleDisconnect(conn.provider);
					} else {
						handleConnect(conn.provider);
					}
				}}
				disabled={buttonDisabled}
			>
				{(isThisActionPending || isLoading) && (
					<LoaderCircleIcon data-icon="inline-start" className="animate-spin" />
				)}
				{isLoading ? "Loading" : conn.connected ? "Disconnect" : "Connect"}
			</Button>
		);
		const buttonOrTooltip =
			conn.disabledReason && conn.connected ? (
				<Tooltip>
					<TooltipTrigger render={<span className="inline-flex" />}>
						{button}
					</TooltipTrigger>
					<TooltipContent>{conn.disabledReason}</TooltipContent>
				</Tooltip>
			) : (
				button
			);
		return (
			<div
				key={conn.id}
				className="flex min-h-[88px] items-center gap-4 rounded-lg border bg-card px-4 py-3 text-sm shadow-sm"
			>
				{conn.icon}
				<div className="min-w-0 flex-1">
					<div className="font-medium leading-tight">{conn.name}</div>
					<div className="mt-1 text-sm text-muted-foreground">
						{conn.description}
					</div>
					<div className="mt-1 line-clamp-1 text-sm text-muted-foreground">
						{isLoading ? (
							<span className="inline-block h-4 w-40 animate-pulse rounded bg-muted" />
						) : (
							conn.detail
						)}
					</div>
				</div>
				<div className="flex shrink-0 items-center gap-2">
					{buttonOrTooltip}
				</div>
			</div>
		);
	}

	return (
		<TooltipProvider>
			<div className="flex h-full flex-col">
				<header className="flex shrink-0 items-center border-b py-3 pl-14 pr-6">
					<h1 className="text-lg font-semibold">Connections</h1>
				</header>
				<ScrollArea className="min-h-0 flex-1">
					<div className="p-6">
						<div className="mx-auto flex max-w-4xl flex-col gap-8">
							{locationState?.googleConnectionStatus === "connected" && (
								<Alert>
									<AlertTitle>Google connected</AlertTitle>
									<AlertDescription>
										Your Google account is connected and ready for workflows.
									</AlertDescription>
								</Alert>
							)}
							{localMessage && (
								<Alert>
									<AlertTitle>Connection update</AlertTitle>
									<AlertDescription>{localMessage}</AlertDescription>
								</Alert>
							)}

							<section className="flex flex-col gap-3">
								<div>
									<h2 className="text-base font-semibold tracking-tight">
										Personal connections
									</h2>
									<p className="mt-1 text-sm text-muted-foreground">
										Tied to your account. Other teammates connect their own.
									</p>
								</div>
								<div className="flex flex-col gap-3">
									{personalCards.map(renderCard)}
								</div>
							</section>

							<section className="flex flex-col gap-3">
								<div>
									<h2 className="text-base font-semibold tracking-tight">
										Firm connections
									</h2>
									<p className="mt-1 text-sm text-muted-foreground">
										Shared by the whole firm. Only the connecting user or a
										Clerical staff member can disconnect.
									</p>
								</div>
								<div className="flex flex-col gap-3">
									{firmCards.map(renderCard)}
								</div>
							</section>

							{/* Docketwise connect dialog */}
							<Dialog
								open={dwDialogOpen}
								onOpenChange={(open) => {
									setDwDialogOpen(open);
									if (!open) resetDwForm();
								}}
							>
								<DialogContent className="sm:max-w-md">
									<DialogHeader>
										<DialogTitle>Connect Docketwise</DialogTitle>
										<DialogDescription>
											Enter your Docketwise login. We'll verify the credentials
											by signing in before saving them.
										</DialogDescription>
									</DialogHeader>
									<div className="flex flex-col gap-3">
										<Input
											placeholder="Email"
											type="email"
											autoComplete="username"
											value={dwUsername}
											onChange={(e) => setDwUsername(e.target.value)}
											disabled={dwConnecting}
										/>
										<Input
											placeholder="Password"
											type="password"
											autoComplete="current-password"
											value={dwPassword}
											onChange={(e) => setDwPassword(e.target.value)}
											disabled={dwConnecting}
										/>
										{dwError && (
											<p className="text-xs text-destructive">{dwError}</p>
										)}
									</div>
									<DialogFooter>
										<Button
											size="sm"
											onClick={handleDocketwiseConnect}
											disabled={dwConnecting}
										>
											{dwConnecting && (
												<LoaderCircleIcon
													data-icon="inline-start"
													className="animate-spin"
												/>
											)}
											Connect
										</Button>
									</DialogFooter>
								</DialogContent>
							</Dialog>

							{/* Microsoft Teams setup dialog */}
							<Dialog open={teamsDialogOpen} onOpenChange={setTeamsDialogOpen}>
								<DialogContent className="sm:max-w-2xl">
									<ScrollArea className="max-h-[calc(85vh-2rem)]">
										<div className="flex flex-col gap-4">
											<DialogHeader>
												<DialogTitle>
													Install Clerical in Microsoft Teams
												</DialogTitle>
												<DialogDescription>
													The Clerical bot is firm-wide — anyone in your
													Microsoft tenant can chat with it once it's uploaded
													as a custom app. Follow the steps below, or see
													Microsoft's{" "}
													<a
														className="underline"
														href="https://learn.microsoft.com/en-us/microsoftteams/platform/concepts/deploy-and-publish/apps-upload"
														target="_blank"
														rel="noreferrer"
													>
														official upload guide
													</a>
													.
												</DialogDescription>
											</DialogHeader>
											<div className="flex flex-col gap-4 text-sm">
												<div className="flex items-center justify-between gap-3 rounded-md border bg-muted/40 p-3">
													<div>
														<div className="font-medium">
															Step 1 — Download the app package
														</div>
														<div className="mt-0.5 text-muted-foreground">
															This is the zip you'll upload into Teams.
														</div>
													</div>
													{teamsPackage?.available && teamsAppDownloadUrl ? (
														<a
															href={teamsAppDownloadUrl}
															className="inline-flex h-8 shrink-0 items-center justify-center whitespace-nowrap rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground shadow hover:bg-primary/90"
														>
															Download teams-app.zip
														</a>
													) : (
														<span className="inline-flex h-8 shrink-0 items-center justify-center whitespace-nowrap rounded-md bg-muted px-3 text-xs font-medium text-muted-foreground">
															{teamsPackage === undefined
																? "Loading…"
																: "Package not yet uploaded"}
														</span>
													)}
												</div>

												<ol className="ml-5 list-decimal space-y-4">
													<li>
														<div>
															Make sure custom app upload is enabled for your
															tenant. See{" "}
															<a
																className="underline"
																href="https://learn.microsoft.com/en-us/microsoftteams/platform/concepts/build-and-test/prepare-your-o365-tenant#enable-custom-teams-apps-and-configure-custom-app-upload-settings"
																target="_blank"
																rel="noreferrer"
															>
																Enable custom app upload
															</a>{" "}
															if your admin hasn't done this yet.
														</div>
													</li>
													<li>
														<div>
															Open Microsoft Teams, go to <strong>Apps</strong>{" "}
															→ <strong>Manage your apps</strong>, then click{" "}
															<strong>Upload an app</strong>.
														</div>
														<img
															className="mt-2 w-full rounded-md border"
															src="https://learn.microsoft.com/en-us/microsoftteams/platform/assets/images/publish-app/upload-an-app.png"
															alt="Teams Apps page with the Upload an app option highlighted"
														/>
													</li>
													<li>
														<div>
															Select <strong>Upload a custom app</strong>.
														</div>
														<img
															className="mt-2 w-full rounded-md border"
															src="https://learn.microsoft.com/en-us/microsoftteams/platform/assets/images/publish-app/upload-custom-app.png"
															alt="Upload a custom app dialog in Teams"
														/>
													</li>
													<li>
														<div>
															Pick the <code>teams-app.zip</code> you downloaded
															above, then click <strong>Add</strong>.
														</div>
														<img
															className="mt-2 w-full rounded-md border"
															src="https://learn.microsoft.com/en-us/microsoftteams/platform/assets/images/publish-app/teams-app-detail.png"
															alt="Custom app details dialog with the Add button"
														/>
													</li>
													<li>
														<div>
															Click <strong>Open</strong> for personal use, or
															pick a team / chat to install{" "}
															<strong>Clerical Agent</strong> firm-wide and
															select <strong>Go</strong>.
														</div>
														<img
															className="mt-2 w-full rounded-md border"
															src="https://learn.microsoft.com/en-us/microsoftteams/platform/assets/images/publish-app/teams-app-scope.png"
															alt="Scope selection dialog for installing the app"
														/>
													</li>
													<li>
														Send a test message to{" "}
														<strong>Clerical Agent</strong>. This page will
														switch to "Connected" once we see the first message
														from your tenant.
													</li>
												</ol>
											</div>
											<DialogFooter>
												<Button
													size="sm"
													variant="outline"
													onClick={() => setTeamsDialogOpen(false)}
												>
													Done
												</Button>
											</DialogFooter>
										</div>
									</ScrollArea>
								</DialogContent>
							</Dialog>

							{/* MiraTech INSZoom connect dialog */}
							<Dialog
								open={izDialogOpen}
								onOpenChange={(open) => {
									setIzDialogOpen(open);
									if (!open) resetIzForm();
								}}
							>
								<DialogContent className="sm:max-w-md">
									<DialogHeader>
										<DialogTitle>Connect MiraTech INSZoom</DialogTitle>
										<DialogDescription>
											Enter your MiraTech INSZoom login. Credentials are saved
											securely and used when workflows need to reach INSZoom.
										</DialogDescription>
									</DialogHeader>
									<div className="flex flex-col gap-3">
										<Input
											placeholder="Email"
											type="email"
											autoComplete="username"
											value={izUsername}
											onChange={(e) => setIzUsername(e.target.value)}
											disabled={izConnecting}
										/>
										<Input
											placeholder="Password"
											type="password"
											autoComplete="current-password"
											value={izPassword}
											onChange={(e) => setIzPassword(e.target.value)}
											disabled={izConnecting}
										/>
										{izError && (
											<p className="text-xs text-destructive">{izError}</p>
										)}
									</div>
									<DialogFooter>
										<Button
											size="sm"
											onClick={handleInszoomConnect}
											disabled={izConnecting}
										>
											{izConnecting && (
												<LoaderCircleIcon
													data-icon="inline-start"
													className="animate-spin"
												/>
											)}
											Connect
										</Button>
									</DialogFooter>
								</DialogContent>
							</Dialog>
						</div>
					</div>
				</ScrollArea>
			</div>
		</TooltipProvider>
	);
}
